import { Listbox, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Fragment } from "react";
import Header from "../../../../components/header";
import {
  ChartOrderByEnum,
  ChartStatusEnum,
  ChartTypeEnum,
} from "../../../../models/chart";
import { ChartsState } from "../hooks/use-charts";

type ChartsHeaderProps = {
  state: ChartsState;
};

const ChartsHeader = (props: ChartsHeaderProps) => {
  const chartsState: ChartsState = props.state;

  const getChartTypeName = () => {
    if (chartsState.chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "General";
    } else if (chartsState.chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "Partisan";
    } else if (chartsState.chartType === ChartTypeEnum.NON_PARTISAN) {
      return "Non-Partisan";
    } else if (chartsState.chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "Presidential";
    }
  };

  const getHeaderTitle = () => {
    if (chartsState.chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "Find a General Chart";
    } else if (chartsState.chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "Find a Partisan Primary Chart";
    } else if (chartsState.chartType === ChartTypeEnum.NON_PARTISAN) {
      return "Find a Non-Partisan Chart";
    } else if (chartsState.chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "Find a Presidential Chart";
    }
  };

  const splitDate = chartsState.selectedElectionDate.split("T")[0].split("-");
  return (
    <>
      <Header>
        <div className="container w-full mx-auto">
          <div className="container mx-auto mb-6 text-3xl font-semibold text-white">
            {getHeaderTitle()}
          </div>
          <div className="container flex items-center gap-2 px-6 py-2 mx-auto mb-6 bg-white rounded-full">
            <MagnifyingGlassIcon className="w-8 h-8 text-gray-500" />
            <input
              key="search-input"
              className="w-full m-2 text-lg outline-none"
              placeholder="Search Charts by Municipality, Precinct or Keyword"
              value={chartsState.query}
              onChange={(e) => chartsState.setQuery(e.target.value)}
            />
          </div>
        </div>
      </Header>
      <div className="p-12 pt-6 pb-6">
        <div className="flex items-center justify-between py-6 border-b">
          <div className="flex gap-2">
            <div className="relative">
              <Listbox
                value={chartsState.status}
                onChange={(e) => {
                  chartsState.updateStatus(e);
                }}
              >
                <div className="relative h-full">
                  <Listbox.Button className="flex items-center justify-between px-4 py-2 bg-white border rounded-md border-primary-blue min-w-64">
                    <div>
                      Filter by Status:{" "}
                      <span className="pl-2 font-semibold text-primary-blue">
                        {ChartStatusEnum[chartsState.status]}
                      </span>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 ml-8" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {Object.keys(ChartStatusEnum).map((status) => (
                        <Listbox.Option
                          key={`general-chart-status-${status}`}
                          className={({ active }) =>
                            `cursor-default select-none relative py-2 pl-10 pr-4 ${active
                              ? "text-blue-900 bg-blue-100"
                              : "text-gray-900"
                            }`
                          }
                          value={status}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"
                                  }`}
                              >
                                {
                                  ChartStatusEnum[
                                  status as keyof typeof ChartStatusEnum
                                  ]
                                }
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            <div className="relative">
              <Listbox
                value={chartsState.orderBy}
                onChange={(e) => {
                  chartsState.updateOrderBy(e);
                }}
              >
                <div className="relative h-full">
                  <Listbox.Button className="flex items-center justify-between px-4 py-2 bg-white border rounded-md border-primary-blue min-w-64">
                    <div>
                      Sort by:{" "}
                      <span className="pl-2 font-semibold text-primary-blue">
                        {ChartOrderByEnum[chartsState.orderBy]}
                      </span>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 ml-8" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {Object.keys(ChartOrderByEnum).map((orderBy) => (
                        <Listbox.Option
                          key={`general-chart-order-${orderBy}`}
                          className={({ active }) =>
                            `cursor-default select-none relative py-2 pl-10 pr-4 ${active
                              ? "text-blue-900 bg-blue-100"
                              : "text-gray-900"
                            }`
                          }
                          value={orderBy}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"
                                  }`}
                              >
                                {
                                  ChartOrderByEnum[
                                  orderBy as keyof typeof ChartOrderByEnum
                                  ]
                                }
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              chartsState.startNewChart();
            }}
            className="uppercase inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 border border-primary-blue text-base font-semibold rounded-md shadow-sm text-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Create chart
          </button>
        </div>
        {chartsState.charts.length > 0 && <div className="col-start-3 col-end-4 row-start-1 row-end-3 py-8">
          <div
            className="flex flex-col items-center justify-center w-full h-full p-8 border-4 border-white rounded-lg shadow-md"
            style={{
              backgroundImage: "url('/charts/charts-background.png')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          >
            <div className='flex flex-row justify-between w-full'>
              <div className='w-5/6 pl-4 pr-10'>
                <div className="text-xl font-semibold text-primary-blue mb-[10px]">
                  Let Us Print Your Charts
                </div>
                <div className="text-left">
                  <span className="font-medium">
                    Having your charts printed and delivered has never
                    been easier.
                  </span>{" "}
                  For a simple fee of $10 each, ElectionSource will
                  print them using our quality inkjet printer and mail
                  them straight to you via USPS.
                </div>
              </div>
            </div>
          </div>
        </div>}
        {chartsState.status === "InProgress" && chartsState.charts.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex text-2xl font-semibold text-primary-blue">
              <Listbox
                value={chartsState.selectedElectionDate}
                onChange={(e) => chartsState.setSelectedElectionDate(e)}
              >
                <div className="relative h-full">
                  <Listbox.Button className="flex items-center justify-between font-semibold">
                    <div>
                      {new Date(
                        `${splitDate[1]}-${splitDate[2]}-${splitDate[0]}`
                      ).toLocaleString("en", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <ChevronDownIcon className="w-8 h-8 mb-1 ml-2" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {chartsState.electionDates.map((electionDate: any) => (
                        <Listbox.Option
                          key={`general-chart-election-date-${electionDate}`}
                          className={({ active }) =>
                            `cursor-default select-none relative py-2 pl-10 pr-4 ${active
                              ? "text-blue-900 bg-blue-100"
                              : "text-gray-900"
                            }`
                          }
                          value={electionDate}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-medium" : "font-normal"
                                  }`}
                              >
                                {new Date(`${electionDate.split('T')[0].split('-')[1]}-${electionDate.split('T')[0].split('-')[2]}-${electionDate.split('T')[0].split('-')[0]}`).toLocaleString("en", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                })}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
            <div className="flex gap-[20px] items-center">
              <div className="">
                TOTAL:{" "}
                <span className="font-semibold">${chartsState.total}.00</span>
              </div>
              <button
                onClick={chartsState.finishAndInvoice}
                className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Finish & Invoice
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChartsHeader;
